import * as THREE from 'three';

export type RadiusFacadeProfile = 'radius_convex' | 'radius_concave';

export function isRadiusFacadeProfile(value: unknown): value is RadiusFacadeProfile {
  const text = String(value ?? '').trim().toLowerCase();
  return text === 'radius_convex' || text === 'radius_concave';
}

/**
 * Central bulge (sagitta) of a radius facade in mm.  Grows with panel width but
 * caps at 38mm so wide doors don't balloon.  Zero for zero-width input.
 */
export function radiusFacadeBulgeMm(widthMm: number): number {
  const w = Math.max(0, Number.isFinite(widthMm) ? widthMm : 0);
  return Math.min(38, w * 0.09);
}

/**
 * Signed Z offset of the arc mid-surface at horizontal position `x` (measured
 * from the panel centre, x ∈ [-w/2, w/2]).  Uses a true circular arc through the
 * two edge points (offset 0) and the apex (offset = ±bulge at the centre).
 *
 *   convex  → centre pushed toward +Z (door face bows outward / toward viewer)
 *   concave → centre pulled toward -Z (door face bows inward / away)
 */
function makeArcOffset(widthMm: number, profile: RadiusFacadeProfile): (x: number) => number {
  const a = Math.max(0.5, widthMm / 2);
  const bulge = radiusFacadeBulgeMm(widthMm);
  const sign = profile === 'radius_concave' ? -1 : 1;
  if (bulge <= 1e-3) return () => 0;
  // Circle through (±a, 0) and (0, bulge): centre at (0, cz), radius R.
  const R = (bulge * bulge + a * a) / (2 * bulge);
  const cz = (bulge * bulge - a * a) / (2 * bulge);
  return (x: number) => {
    const inside = Math.max(0, R * R - x * x);
    return sign * (cz + Math.sqrt(inside));
  };
}

/**
 * Build a smoothly curved facade panel geometry.
 *
 * Implementation: start from a width-segmented THREE.BoxGeometry (so faces,
 * winding, UVs and cap topology are already correct) and displace every vertex
 * along Z by the circular-arc offset for its X.  Recomputing vertex normals
 * yields a smooth front/back surface while keeping the caps/top/bottom flat.
 * Overall thickness (d) is preserved — the whole slab bows together.
 *
 * @param sizeMm   [width, height, depth] of the facade in mm.
 * @param profile  radius_convex (bows +Z) or radius_concave (bows -Z).
 * @param segments horizontal subdivisions of the arc (default 24).
 */
export function buildRadiusFacadeGeometry(
  sizeMm: [number, number, number],
  profile: RadiusFacadeProfile,
  segments = 24,
): THREE.BufferGeometry {
  const w = Math.max(1, sizeMm[0]);
  const h = Math.max(1, sizeMm[1]);
  const d = Math.max(1, sizeMm[2]);
  const seg = Math.max(2, Math.floor(segments));

  const geometry = new THREE.BoxGeometry(w, h, d, seg, 1, 1);
  const offsetAt = makeArcOffset(w, profile);

  const position = geometry.getAttribute('position') as THREE.BufferAttribute;
  for (let i = 0; i < position.count; i += 1) {
    const x = position.getX(i);
    const z = position.getZ(i);
    position.setZ(i, z + offsetAt(x));
  }
  position.needsUpdate = true;

  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}
